import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from './i18n';
import { authFetch, isAuthenticated } from './auth';
import FoodSheetForm from './FoodSheetForm';
import FoodSheetItem from './FoodSheetItem';
import { ComparisonTable, FOOD_COMPARISON_SPEC } from './WineCatalog';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8100/api'
  : '/api';

function DishDetail({ dishId, onBack }) {
  const { t, tc, formatDate, formatNumber } = useI18n();
  const [pairingData, setPairingData] = useState(null);
  const [error, setError] = useState(null);
  const [foodSheets, setFoodSheets] = useState([]);
  const [meId, setMeId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [note, setNote] = useState(null);

  const loadFoodSheets = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/dishes/${dishId}/food-sheets`);
      const data = res && res.ok ? await res.json() : { sheets: [] };
      setFoodSheets(data.sheets || []);
    } catch { /* ignore */ }
  }, [dishId]);

  useEffect(() => {
    let abort = false;
    loadFoodSheets();
    if (isAuthenticated()) {
      authFetch(`${API_URL}/me`)
        .then((r) => (r && r.ok ? r.json() : null))
        .then((u) => { if (!abort && u) setMeId(u.id); })
        .catch(() => {});
    }
    authFetch(`${API_URL}/dishes/${dishId}/pairings`)
      .then((r) => (r && r.ok ? r.json() : Promise.reject(r && r.status)))
      .then((d) => { if (!abort) setPairingData(d); })
      .catch((e) => { if (!abort) setError(e || true); });
    return () => { abort = true; };
  }, [dishId, loadFoodSheets]);

  const removeFoodSheet = async (sheet) => {
    if (!window.confirm(t('catalog.food.confirm_delete'))) return;
    try {
      const res = await authFetch(`${API_URL}/food-sheets/${sheet.id}`, { method: 'DELETE' });
      if (!res || !res.ok) throw new Error();
      setNote({ type: 'success', text: t('catalog.food.deleted') });
      loadFoodSheets();
    } catch {
      setNote({ type: 'error', text: t('catalog.food.delete_error') });
    }
  };

  if (error) return <div className="catalog"><button className="back-btn" onClick={onBack}>{t('catalog.back')}</button><p>{t('catalog.err_dish')}</p></div>;
  if (!pairingData) return <div className="catalog"><button className="back-btn" onClick={onBack}>{t('catalog.back')}</button><p>{t('ui.loading')}</p></div>;

  const dish = pairingData.dish;
  if (editing) {
    return (
      <div className="catalog">
        <FoodSheetForm initialData={editing} onCancel={() => setEditing(null)}
          onDone={() => { setEditing(null); setNote({ type: 'success', text: t('catalog.food.updated') }); loadFoodSheets(); }} />
      </div>
    );
  }
  return (
    <div className="catalog">
      <button className="back-btn" onClick={onBack}>{t('catalog.back_dishes')}</button>
      <div className="dish-card">
        <h2>{dish.name}</h2>
        {dish.description && <p>{dish.description}</p>}
      </div>

      {note && <div className={`message ${note.type}`}>{note.text}</div>}
      <h3>{t('catalog.food_title', { n: foodSheets.length })}</h3>
      {foodSheets.length === 0 ? <p className="empty">{t('catalog.food_none')}</p> : (
        <>
          <ul className="sheet-list">
            {foodSheets.map((sheet, index) => (
              <FoodSheetItem key={sheet.id} sheet={sheet} meId={meId} heading={`#${index + 1}`}
                onEdit={setEditing} onDelete={removeFoodSheet} />
            ))}
          </ul>
          <ComparisonTable title={t('catalog.food_short')} ns="pairing" rows={foodSheets} spec={FOOD_COMPARISON_SPEC} />
        </>
      )}

      <h3>{t('catalog.pairing_title', { n: pairingData.pairings.length })}</h3>
      {pairingData.pairings.length === 0 ? <p className="empty">{t('catalog.none_for_dish')}</p> : (
        <ul className="sheet-list">
          {pairingData.pairings.map((pairing) => (
            <li key={pairing.id} className="sheet-summary">
              <span>
                <strong>{pairing.wine_name}</strong>
                {pairing.vintage ? ` ${pairing.vintage}` : ''}
                {pairing.producer ? ` — ${pairing.producer}` : ''}
                {' · '}{formatDate(pairing.tasting_date) || '—'}
              </span>
              <span className="score">
                {tc('outcome', pairing.pairing_outcome) || '—'}
                {pairing.score != null && <em> ({formatNumber(pairing.score)}/10)</em>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DishCatalog() {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `${API_URL}/dishes${query ? `?q=${encodeURIComponent(query)}` : ''}`;
        const res = await fetch(url, { signal: controller.signal });
        if (res.ok) setDishes(await res.json());
      } catch { /* aborted */ } finally { setLoading(false); }
    }, query ? 250 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query]);

  if (selectedId) return <DishDetail dishId={selectedId} onBack={() => setSelectedId(null)} />;

  return (
    <div className="catalog">
      <h2>{t('catalog.dishes_title')}</h2>
      <div className="catalog-search">
        <input
          type="search"
          placeholder={t('catalog.dishes_search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {loading && <p className="empty">{t('ui.loading')}</p>}
      {!loading && dishes.length === 0 && <p className="empty">{t('catalog.dishes_none')}</p>}
      {dishes.length > 0 && (
        <table className="catalog-table">
          <thead>
            <tr><th>{t('catalog.th_dish')}</th><th>{t('catalog.th_desc')}</th></tr>
          </thead>
          <tbody>
            {dishes.map((dish) => (
              <tr key={dish.id} onClick={() => setSelectedId(dish.id)}>
                <td><strong>{dish.name}</strong></td>
                <td>{dish.description || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default DishCatalog;
